import { useCallback } from "react";
import RestEndpointsEnum from "../../../models/RestEndpointsEnum";
import IOnGetVehicleParLevelsDTO from "../../../models/DTO/IOnGetVehicleParLevelsDTO";
import ISetVehicleParLevelsDTO from "../../../models/DTO/ISetVehicleParLevelsDTO";
import { buildRestUrl, buildAuthHeadersForFetch } from "../../../services/RestService";
import { useAppContextNonNull } from "@contexts/AppContext";
import { IParLevelEditorItem, IParLevelEditorLine, ParLevelsEditor } from "../../shared/ParLevelsEditor/ParLevelsEditor";

type Props = {
    vehicleGuid: string;
};

/**
 * Vehicle (truck) par editor: the shared par grid over GET|PUT /v1/par_levels/vehicles/:guid.
 * QoH is shown for context; a par of 0 means "no floor" but the row stays (it carries truck stock).
 */
export const VehicleParLevels = (props: Props) => {
    const { userModel, supplierModel } = useAppContextNonNull();

    const loadFn = useCallback(async (): Promise<IParLevelEditorItem[]> => {
        const url = buildRestUrl({
            endpoint: RestEndpointsEnum.GetVehicleParLevels,
            params: { vehicleGuid: props.vehicleGuid },
        });

        const response = await fetch(url, { method: "GET", headers: buildAuthHeadersForFetch({ userModel, supplierModel }) });

        if (!response.ok) {
            throw new Error(`GET vehicle par levels failed: ${response.status}`);
        }

        const data: IOnGetVehicleParLevelsDTO = await response.json();

        return data.items;
    }, [props.vehicleGuid, userModel, supplierModel]);

    const saveFn = async (items: IParLevelEditorLine[]): Promise<IParLevelEditorItem[]> => {
        const payload: ISetVehicleParLevelsDTO = { items };

        const url = buildRestUrl({
            endpoint: RestEndpointsEnum.SetVehicleParLevels,
            params: { vehicleGuid: props.vehicleGuid },
        });

        const response = await fetch(url, {
            method: "PUT",
            headers: buildAuthHeadersForFetch({ userModel, supplierModel }),
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            throw new Error(`PUT vehicle par levels failed: ${response.status}`);
        }

        // PUT returns the full GET shape, so hydrate from it instead of re-fetching.
        const data: IOnGetVehicleParLevelsDTO = await response.json();

        return data.items;
    };

    return (
        <ParLevelsEditor
            idPrefix={"vehicle-par-levels"}
            loadFn={loadFn}
            saveFn={saveFn}
            showQtyOnHand={true}
            helpKey={"vehicle_par_help"}
            zeroNoteKey={"vehicle_par_zero_note"}
        />
    );
};
